import type { Post } from '../types';

/**
 * Before/after post pairing (see PostCard, structureBadges.ts) is
 * display-only. An "after" post carries `beforePostId` pointing at an
 * earlier standalone "before" post, but the before post itself is never
 * modified — so it would otherwise render twice: once on its own, once
 * inside the after's pair card. Suppression can't be enforced server-side:
 * firestore.rules only allows author-only writes, and an after can be
 * authored by someone other than the before's author, so the before doc has
 * no way to be flagged when it's paired. Every list that renders posts must
 * hide a paired-away before at render time instead. The before stays
 * reachable via the pair's "view the before post" link and direct post
 * links — never filter post detail.
 */

/** Every `beforePostId` referenced by an after post in `posts`. */
export function collectPairedBeforeIds(posts: Post[]): Set<string> {
  const ids = new Set<string>();
  for (const p of posts) {
    if (p.beforePostId) ids.add(p.beforePostId);
  }
  return ids;
}

/**
 * `posts` minus any post whose id is referenced as a `beforePostId` by
 * something in `knownPosts`. `knownPosts` defaults to `posts` but can be a
 * broader array (e.g. including posts not in the rendered list yet) so a
 * before gets suppressed as soon as its after is known anywhere on the
 * client, even before the before itself has loaded.
 */
export function suppressPairedBefores(posts: Post[], knownPosts: Post[] = posts): Post[] {
  const pairedBeforeIds = collectPairedBeforeIds(knownPosts);
  if (pairedBeforeIds.size === 0) return posts;
  const filtered = posts.filter((p) => !pairedBeforeIds.has(p.id));
  return filtered.length === posts.length ? posts : filtered;
}
